import { useState } from "react";
import { Upload, Trash2, Loader2, ZoomIn } from "lucide-react";
import { authHeaders } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Reusable document upload card (used for ID front/back, license front/back).
 *  - `value` = current URL (or null)
 *  - `onChange(url)` called after successful upload or clear
 *  - `label`, `description` for UI
 */
export default function DocumentUpload({ value, onChange, label, description, testIdBase = "doc" }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${BACKEND_URL}/api/uploads/customer-document`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      onChange(data.url);
    } catch (e) {
      alert(e.message);
    } finally {
      setUploading(false);
    }
  };

  const fullUrl = value?.startsWith("http") ? value : (value ? `${BACKEND_URL}${value}` : null);

  return (
    <>
      <div className="rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden" data-testid={`${testIdBase}-card`}>
        {fullUrl ? (
          <div className="relative aspect-[16/10] bg-zinc-900">
            <img src={fullUrl} alt={label} className="w-full h-full object-cover" />
            <div className="absolute top-2 right-2 flex gap-1">
              <button onClick={() => setPreview(true)}
                      className="bg-zinc-900/80 hover:bg-zinc-900 text-white rounded-full p-1.5"
                      data-testid={`${testIdBase}-zoom`}>
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onChange(null)}
                      className="bg-red-500/80 hover:bg-red-500 text-white rounded-full p-1.5"
                      data-testid={`${testIdBase}-remove`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-widest bg-emerald-500/80 text-white px-2 py-0.5 rounded-full">✓ Caricato</span>
          </div>
        ) : (
          <label className="aspect-[16/10] flex flex-col items-center justify-center cursor-pointer hover:bg-zinc-900/60 transition" data-testid={`${testIdBase}-empty`}>
            {uploading ? (
              <Loader2 className="w-6 h-6 text-[#FFD700] animate-spin" />
            ) : (
              <>
                <Upload className="w-6 h-6 text-zinc-500 mb-2" />
                <span className="text-xs text-zinc-400 uppercase tracking-wider">Carica / Scatta foto</span>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => upload(e.target.files?.[0])}
              hidden
              data-testid={`${testIdBase}-input`}
            />
          </label>
        )}
        <div className="px-3 py-2 border-t border-zinc-800 bg-zinc-900/30">
          <p className="text-xs font-medium text-zinc-200">{label}</p>
          {description && <p className="text-[10px] text-zinc-500 mt-0.5">{description}</p>}
        </div>
      </div>

      {preview && fullUrl && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setPreview(false)}>
          <img src={fullUrl} alt={label} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </>
  );
}
